import { Injectable, NgZone } from '@angular/core';
import * as RecordRTC from 'recordrtc';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';

interface RecordedAudioOutput {
  blob: Blob;
  title: string;
}

@Injectable()
export class AudioRecordingService {

  private stream;
  private recorder;
  private interval;
  private startTime;
  private _recorded = new Subject<RecordedAudioOutput>();
  private _recordingTime = new Subject<number>();
  private _recordingFailed = new Subject<string>();

  constructor() {

  }

  getRecordedBlob(): Observable<RecordedAudioOutput> {
    return this._recorded.asObservable();
  }

  getRecordedTime(): Observable<number> {
    return this._recordingTime.asObservable();
  }

  recordingFailed(): Observable<string> {
    return this._recordingFailed.asObservable();
  }

  startRecording() {

    if (this.recorder) {
      // It means recording is already started or it is already recording something
      return;
    }

    this._recordingTime.next(0);
    navigator.mediaDevices.getUserMedia({ audio: true }).then(s => {
      this.stream = s;
      this.record();
    }).catch(error => {
      this._recordingFailed.next();
    });

  }

  private record() {
    this.recorder = new RecordRTC.StereoAudioRecorder(this.stream, {
      type: 'audio',
      mimeType: 'audio/wav',
      // get16BitAudio: true,
      sampleRate: 48000,
      numberOfAudioChannels: 2
    });

    this.recorder.record();
    this.startTime = moment();
    this.interval = setInterval(
      () => {
        const currentTime = moment();
        const diffTime = moment.duration(currentTime.diff(this.startTime));

        const time = diffTime.asSeconds()
        this._recordingTime.next(time);
        console.log(`currentTime ${currentTime} ,this.startTime ${this.startTime}  ,diffTime ${diffTime} , time ${time} `)
      },
      1000
    );
  }

  private toString(value) {
    let val = value;
    if (!value) {
      val = '00';
    }
    if (value < 10) {
      val = '0' + value;
    }
    return val;
  }

  // async stopRecording(waveName) {
  //   if (this.recorder) {
  //     this.recorder.stop((blob) => {
  //       if (this.startTime) {
  //         const wavName = encodeURIComponent(waveName + '.wav');
  //         this.stopMedia();
  //         this._recorded.next({ blob: blob, title: wavName });
  //       }
  //     }, () => {
  //       this.stopMedia();
  //       this._recordingFailed.next();
  //     });
  //   }
  // }

  async stopRecording(waveName) {
    if (!this.recorder) return;

    this.recorder.stop(async (blob) => {
      try {
        if (this.startTime) {
          const downsampledBlob = await this.convertToMono16k(blob);
          const wavName = encodeURIComponent(waveName + '.wav');
          this.stopMedia();
          this._recorded.next({ blob: downsampledBlob, title: wavName });
        }
      } catch (err) {
        console.error('Downsampling failed:', err);
        this.stopMedia();
        this._recordingFailed.next();
      }
    }, (error) => {
      console.error('Recording stop error:', error);
      this.stopMedia();
      this._recordingFailed.next();
    });
  }

  private async convertToMono16k(blob: Blob): Promise<Blob> {
    const arrayBuffer = await blob.arrayBuffer();
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const decoded = await audioCtx.decodeAudioData(arrayBuffer);

    // Convert stereo to mono
    const input = decoded.getChannelData(0);
    const sampleRate = decoded.sampleRate;
    const targetRate = 16000;

    // Downsample
    const ratio = sampleRate / targetRate;
    const newLength = Math.round(input.length / ratio);
    const result = new Float32Array(newLength);

    for (let i = 0, j = 0; i < newLength; i++, j += ratio) {
      result[i] = input[Math.floor(j)];
    }

    // Encode to WAV (mono 16k)
    const wavBuffer = this.encodeWAV(result, targetRate);
    return new Blob([wavBuffer], { type: 'audio/wav' });
  }

  private encodeWAV(samples: Float32Array, sampleRate: number): ArrayBuffer {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    const writeString = (offset, str) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    let offset = 0;
    writeString(offset, 'RIFF'); offset += 4;
    view.setUint32(offset, 36 + samples.length * 2, true); offset += 4;
    writeString(offset, 'WAVE'); offset += 4;
    writeString(offset, 'fmt '); offset += 4;
    view.setUint32(offset, 16, true); offset += 4;
    view.setUint16(offset, 1, true); offset += 2;
    view.setUint16(offset, 1, true); offset += 2; // mono
    view.setUint32(offset, sampleRate, true); offset += 4;
    view.setUint32(offset, sampleRate * 2, true); offset += 4;
    view.setUint16(offset, 2, true); offset += 2;
    view.setUint16(offset, 16, true); offset += 2;
    writeString(offset, 'data'); offset += 4;
    view.setUint32(offset, samples.length * 2, true); offset += 4;

    let pos = offset;
    for (let i = 0; i < samples.length; i++, pos += 2) {
      let s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(pos, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }

    return buffer;
  }


  private stopMedia() {
    if (this.recorder) {
      this.recorder = null;
      clearInterval(this.interval);
      this.startTime = null;
      if (this.stream) {
        this.stream.getAudioTracks().forEach(track => track.stop());
        this.stream = null;
      }
    }
  }

}
